import OrgPageTemplate from '@admin/components/organization/OrgPageTemplate';
import useBreadcrumbs from '@admin/hooks/useAdminBreadcrumbs';
import styled from '@emotion/styled';
import PageHeader from '@shared/components/core/headers/PageHeader';
import SpacedLoader from '@shared/components/loading/SpacedLoader';
import ProfileConflictIndicators from '@shared/components/profile/ProfileConflictIndicators';
import { ProfileOptionsPopover } from '@shared/components/profile/ProfilesOptionPopover';
import { DefaultVoiceLocale } from '@shared/constants/localization/LocalizationConstants';
import { publish } from '@shared/hooks/helpers/EventCallback';
import { extractRoleProperties } from '@shared/hooks/helpers/OrganizationalHelpers';
import { useEditProfileStore } from '@shared/hooks/state/EditProfileContainer';
import { useOrganizationStore } from '@shared/hooks/state/OrganizationContainer';
import useCurrentOrgDetailsFetching from '@shared/hooks/useCurrentOrgDetailsFetching';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatches, useParams } from 'react-router';

export default function ProfileLayout() {
    const { t } = useTranslation();
    const { items } = useBreadcrumbs();
    const { profileId } = useParams();
    const matches = useMatches();
    const isPreview = useEditProfileStore((state) => state.isPreview);
    const name = useEditProfileStore((state) => state.name);
    const {
        currentOrg,
        membership,
        isLoading: orgLoading,
        canEditOrganizationProfiles,
    } = useCurrentOrgDetailsFetching();
    const [isInitialized, setIsInitialized] = useState(false);

    const isNew = profileId?.toLowerCase() === 'new';
    const myRole = membership ? extractRoleProperties(membership) : undefined;
    const canManage = myRole?.canEditOrganizationProfiles || myRole?.isAdmin;

    // Get title and description from route handle, with fallbacks
    const currentMatch = matches[matches.length - 1];
    const handle = currentMatch?.handle as { title?: string; description?: string; showActions?: boolean } | undefined;

    const pageTitle = handle?.title
        ? t(handle.title)
        : isNew
          ? t('common:profile.createTitle')
          : name || t('common:profile.edit');

    const pageDescription = handle?.description ? t(handle.description) : undefined;

    useEffect(() => {
        // Don't update if user has unsaved changes (e.g., copying a profile)
        // This check MUST happen before any other logic
        if (useEditProfileStore.getState().hasChanges) {
            setIsInitialized(true);
            return;
        }

        const getDeviceTypeCustomControls = async () => {
            if (!currentOrg?.id) return;
            const defaultLocale = useEditProfileStore.getState().defaultLocale || DefaultVoiceLocale;
            const result = await useOrganizationStore
                .getState()
                .getOrgAvailableCustomControls(currentOrg.id, defaultLocale);
            const orgAvailableCustomControls = useOrganizationStore.getState().orgAvailableCustomControls;
            if (!result) {
                publish('alertError', t('common:errors.loadCustomControls'));
            } else {
                useEditProfileStore.getState().setAvailableCustomControls(orgAvailableCustomControls);
            }
            console.log('Available custom controls:', orgAvailableCustomControls);
        };

        const initializeProfile = async () => {
            if (orgLoading || !currentOrg?.id) return;
            useEditProfileStore.getState().setOrganizationId(currentOrg.id);

            if (isNew || !profileId) {
                useEditProfileStore.getState().setDefaultProfile(DefaultVoiceLocale);
                setIsInitialized(true);
            } else if (profileId) {
                const success = await useEditProfileStore.getState().loadOrgPreview(profileId);
                useEditProfileStore.getState().setIsPreview(false);
                setIsInitialized(true);
            } else {
                publish('alertError', t('common:errors.loadProfile'));
            }
        };

        setIsInitialized(false);
        initializeProfile();
        getDeviceTypeCustomControls();

        return () => {
            // Only reset if there are no unsaved changes (e.g., not in the middle of copying)
            if (!useEditProfileStore.getState().hasChanges) {
                useEditProfileStore.getState().setDefaultProfile(DefaultVoiceLocale);
                useEditProfileStore.getState().setHasChanges(false);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileId, isNew, currentOrg?.id, orgLoading]);

    if (!isInitialized) return <SpacedLoader />;
    return (
        <OrgPageTemplate>
            <PageHeader title={name || pageTitle} description={pageDescription} items={items}>
                {handle?.showActions && profileId && canEditOrganizationProfiles && (
                    <HeaderActions>
                        <ProfileOptionsPopover disabled={!canManage || isPreview} />
                    </HeaderActions>
                )}
            </PageHeader>
            <ProfileConflictIndicators errorOnly />
            <Outlet />
        </OrgPageTemplate>
    );
}

const HeaderActions = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
`;
